import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  Auth, User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
} from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth   = inject(Auth);
  private fns    = inject(Functions);
  private router = inject(Router);

  private _user$ = new BehaviorSubject<User | null>(null);
  readonly user$: Observable<User | null> = this._user$.asObservable();

  constructor() {
    onAuthStateChanged(this.auth, user => this._user$.next(user));
  }

  get currentUser(): User | null {
    return this._user$.value;
  }

  get isLoggedIn(): boolean {
    return !!this._user$.value;
  }

  /** Register a new donor account */
  async register(email: string, password: string, displayName: string): Promise<void> {
    const cred = await createUserWithEmailAndPassword(this.auth, email, password);
    await updateProfile(cred.user, { displayName });

    // Link this Firebase Auth UID to any existing Donor records with this email
    try {
      const link = httpsCallable(this.fns, 'linkDonorAccount');
      await link({ email: email.toLowerCase() });
    } catch (err) {
      // Non-fatal — account still works, linking can be retried
      console.warn('linkDonorAccount failed (non-fatal):', err);
    }
  }

  /** Sign in with email + password */
  async login(email: string, password: string): Promise<void> {
    await signInWithEmailAndPassword(this.auth, email, password);
  }

  /** Sign out and redirect to /auth */
  async logout(): Promise<void> {
    await signOut(this.auth);
    await this.router.navigate(['/auth']);
  }

  /** Send password reset email */
  async resetPassword(email: string): Promise<void> {
    await sendPasswordResetEmail(this.auth, email);
  }
}
